/**
 * Events between two dates: tutoring shifts from TA Draft's schedule calendar
 */
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import axios from 'axios'

import { TUTORING_SHIFTS_API_PATH, TUTORING_SHIFTS_REFRESH_MS } from 'constants/calendars'
import { ShiftEvent, TutoringShiftsResponse } from 'types/tutoringShifts.types'
import { getShiftEventsBetween } from 'utils/tutoringShifts'

export type UseScheduleEventsOptions = {
  from?: string
  to?: string
}

const fetchTutoringShifts = async (): Promise<TutoringShiftsResponse> => {
  const res = await axios.get<TutoringShiftsResponse>(TUTORING_SHIFTS_API_PATH)

  // The dev server answers unknown paths with index.html, so check the shape
  if (!Array.isArray(res.data?.semesters)) throw new Error('Unexpected tutoring shifts response')
  return res.data
}

const useScheduleEvents = (options: UseScheduleEventsOptions = {}) => {
  const { from, to } = options

  // Tutoring shifts, fetched through this app's own endpoint (the signing happens server-side)
  const {
    data: shiftsData,
    isLoading: loading,
    error: shiftsError,
  } = useQuery(['tutoringShifts'], fetchTutoringShifts, {
    refetchInterval: TUTORING_SHIFTS_REFRESH_MS,
  })

  const events: ShiftEvent[] = useMemo(() => {
    if (!shiftsData || !from || !to) return []
    return getShiftEventsBetween(shiftsData.semesters, from, to)
  }, [shiftsData, from, to])

  const error = shiftsError ? `Tutoring shifts: ${(shiftsError as Error).message}` : ''

  return { events, loading, error }
}

export default useScheduleEvents
